import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import Select from "react-select";

const VendorPaymentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [banks, setBanks] = useState([]);
  const [formData, setFormData] = useState({
    vendor_id: "",
    bank_id: "",
    amount: "",
    payment_date: new Date(),
    payment_method: "",
    reference_no: "",
    description: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchVendors();
    fetchBanks();
    fetchVendorPayment();
  }, [id]);

  const fetchVendorPayment = async () => {
    try {
      const response = await Http.get(`/accounts/vendor-payments/${id}`);
      
      const payment = response.data.data;
      setFormData({
        vendor_id: payment.vendor_id,
        bank_id: payment.bank_id,
        amount: payment.amount,
        payment_date: payment.payment_date ? new Date(payment.payment_date) : new Date(),
        payment_method: payment.payment_method,
        reference_no: payment.reference_no,
        description: payment.description || "",
      });
    } catch (error) {
      console.error("Error fetching vendor payment:", error);
      FormValidation.error("Failed to fetch vendor payment details");
      navigate("/accounts/vendor-payments");
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await Http.get(`/vendors`);
      const vendorOptions = response.data.map((vendor) => ({
        value: vendor.id,
        label: vendor.name,
      }));
      setVendors(vendorOptions);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      FormValidation.error("Failed to fetch vendors");
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await Http.get(`/accounts/banks`);
      const bankOptions = response.data.data.map((bank) => ({
        value: bank.id,
        label: bank.bank_name,
      }));
      setBanks(bankOptions);
    } catch (error) {
      console.error("Error fetching banks:", error);
      FormValidation.error("Failed to fetch banks");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData({
      ...formData,
      [name]: selectedOption ? selectedOption.value : "",
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.vendor_id) newErrors.vendor_id = "Vendor is required";
    if (!formData.bank_id) newErrors.bank_id = "Bank is required";
    if (!formData.amount) newErrors.amount = "Amount is required";
    if (!formData.payment_date) newErrors.payment_date = "Payment date is required";
    if (!formData.payment_method) newErrors.payment_method = "Payment method is required";
    if (!formData.reference_no) newErrors.reference_no = "Reference number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await Http.put(`/accounts/vendor-payments/${id}`, {
        ...formData,
        payment_date: formData.payment_date ? new Date(formData.payment_date).toISOString().split("T")[0] : null,
      });

      FormValidation.success("Vendor payment updated successfully");
      navigate("/accounts-for-supplychain/vendor-payment/list");
    } catch (error) {
      console.error("Error updating vendor payment:", error);
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        FormValidation.error("Failed to update vendor payment");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Edit Vendor Payment</h4>
              <Link to="/accounts/vendor-payments" className="btn btn-primary btn-sm float-right">
                <i className="fa fa-list"></i> Vendor Payment List
              </Link>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Vendor <span className="text-danger">*</span></label>
                      <Select
                        name="vendor_id"
                        options={vendors}
                        value={vendors.find(option => option.value === formData.vendor_id) || null}
                        onChange={(option) => handleSelectChange(option, { name: "vendor_id" })}
                        className={errors.vendor_id ? "is-invalid" : ""}
                        placeholder="Select Vendor"
                        isClearable
                      />
                      {errors.vendor_id && <div className="invalid-feedback">{errors.vendor_id}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Bank <span className="text-danger">*</span></label>
                      <Select
                        name="bank_id"
                        options={banks}
                        value={banks.find(option => option.value === formData.bank_id) || null}
                        onChange={(option) => handleSelectChange(option, { name: "bank_id" })}
                        className={errors.bank_id ? "is-invalid" : ""}
                        placeholder="Select Bank"
                        isClearable
                      />
                      {errors.bank_id && <div className="invalid-feedback">{errors.bank_id}</div>}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Amount <span className="text-danger">*</span></label>
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                        placeholder="Enter Amount"
                      />
                      {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Payment Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        name="payment_date"
                        value={formData.payment_date ? new Date(formData.payment_date).toISOString().split('T')[0] : ''}
                        onChange={handleDateChange}
                        className={`form-control ${errors.payment_date ? "is-invalid" : ""}`}
                      />
                      {errors.payment_date && <div className="invalid-feedback">{errors.payment_date}</div>}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Payment Method <span className="text-danger">*</span></label>
                      <select
                        name="payment_method"
                        value={formData.payment_method}
                        onChange={handleChange}
                        className={`form-control ${errors.payment_method ? "is-invalid" : ""}`}
                      >
                        <option value="">Select Payment Method</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="cash">Cash</option>
                        <option value="check">Check</option>
                        <option value="online">Online Payment</option>
                      </select>
                      {errors.payment_method && <div className="invalid-feedback">{errors.payment_method}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Reference No <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="reference_no"
                        value={formData.reference_no}
                        onChange={handleChange}
                        className={`form-control ${errors.reference_no ? "is-invalid" : ""}`}
                        placeholder="Enter Reference Number"
                      />
                      {errors.reference_no && <div className="invalid-feedback">{errors.reference_no}</div>}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="form-control"
                        rows="3"
                        placeholder="Enter Description"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <button type="submit" className="btn btn-primary" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span className="sr-only">Loading...</span>
                        </>
                      ) : (
                        "Update"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorPaymentEdit;